import { debounce } from '../../../../utils';
import { user } from '../../../../services';
import { Component, Fragment, Event, EventEmitter, h, Prop, Build, Element } from '@stencil/core';
import {
    ChatbotAnswerInterface,
    ChatbotDialogOptionInterface,
    ChatbotEscalationChannelInterface,
    ChatbotEscalationFeedbackInterface,
    ChatbotEscalationInterface,
    ChatbotTextInterface
} from '../../../../services/chatbot';
import { WebChatbotAnswerCustomEvent } from '../../../../components';
import { JSX } from '@stencil/core/internal';

@Component({
    tag: 'web-chatbot-list',
    styleUrls: ['chatbot-list.scss'],
    shadow: false
})
export class ChatbotList {
    @Prop() items: (ChatbotAnswerInterface | string)[] = [];
    @Prop() escalation: ChatbotEscalationInterface;
    @Prop() texts: ChatbotTextInterface;
    @Prop() features: string;
    @Prop() isLivechatAvailable: boolean = false;
    @Event() dialogOption: EventEmitter<{ sessionId: string; value: ChatbotDialogOptionInterface }>;
    @Event() feedback: EventEmitter<ChatbotEscalationFeedbackInterface>;
    @Event() shouldScroll: EventEmitter<unknown>;

    @Element() host: HTMLWebChatbotListElement;
    private observer: MutationObserver;

    /**
     * Handles the click event on a dialog option in the chatbot answer.
     * Emits the dialogOption event with the sessionId and selected value.
     *
     * @param event - The custom event containing sessionId and selected value.
     */
    private onDialogOptionClicked = async (
        event: WebChatbotAnswerCustomEvent<{ sessionId: string; value: ChatbotDialogOptionInterface }>
    ): Promise<void> => {
        event.stopPropagation();
        const { sessionId, value } = event.detail;
        this.dialogOption.emit({ sessionId, value });
    };

    componentDidLoad() {
        this.observeMutations();
    }

    connectedCallback() {
        this.observeMutations();
    }

    disconnectedCallback() {
        this.unobserveMutations();
    }

    private observeMutations() {
        const chatbotList = this.host.querySelector('.chatbot-list');
        if (chatbotList && !this.observer) {
            // Emit shouldScroll on any dom mutations (debounced) within .chatbot-list.
            // Observes subtree for the web-chatbot-escalation component and its internal changes.
            const onMutations = debounce((() => this.shouldScroll.emit()) as MutationCallback, 50);
            this.observer = new MutationObserver(onMutations);
            this.observer.observe(chatbotList, { childList: true, subtree: true });
        }
    }

    private unobserveMutations() {
        if (this.observer) {
            this.observer.disconnect();
            delete this.observer;
        }
    }

    private renderEscalation = (item: ChatbotAnswerInterface) =>
        Object.keys(item.escalation).length > 0 && this.items.indexOf(item) === this.items.length - 1 ? (
            <web-chatbot-escalation options={this.escalation}></web-chatbot-escalation>
        ) : null;

    private renderItem = (item: ChatbotAnswerInterface | string) => {
        switch (typeof item) {
            case 'string':
                return (
                    <div class="chatbot-item chatbot-question-item">
                        <div class="web-balloon right-middle primary padding-large">{item}</div>
                    </div>
                );
            case 'object':
                // Hide channels based on the data we retrieve from CM (previously CX)
                if (this.escalation?.channels && item?.escalation) {
                    this.escalation.channels = this.escalation.channels.map((chan) => ({
                        ...chan,
                        items: chan.items.map((channel) => ({
                            ...channel,
                            hidden: this.hideChannel(item, channel)
                        }))
                    }));
                }

                return (
                    <Fragment>
                        <div class="chatbot-item chatbot-answer-item">
                            <div class="web-balloon left-top secondary padding-large">
                                <web-chatbot-answer
                                    answer={item}
                                    onDialogOption={(event) => this.onDialogOptionClicked(event)}
                                ></web-chatbot-answer>
                            </div>
                        </div>
                        {this.renderEscalation(item)}
                    </Fragment>
                );
        }
    };

    /**
     * Hides a channel based on the visibility property
     * For livechat there is a special case where we check for either 'showChat', 'showChatDE' or 'testChat' properties,
     *   the availibility of operators and the livechat feature flag
     * @param item The answer item.
     * @param channel The channel to hide.
     * @returns True if the channel should be hidden, false otherwise.
     */
    private hideChannel = (item: ChatbotAnswerInterface, channel: ChatbotEscalationChannelInterface): boolean => {
        if (channel.visibilityProperty === 'showChat') {
            if (!this.isLivechatAvailable) {
                return true;
            }
            if (!this.features.includes('livechat')) {
                return true;
            }
            return (
                item.escalation['testChat'] === false ||
                item.escalation['showChat'] === false ||
                item.escalation['showChatDE'] === false
            );
        }
        return item.escalation[channel.visibilityProperty] === false || channel.hidden;
    };

    render = (): JSX.Element => (
        <div class="chatbot-list">
            <div class="chatbot-item chatbot-answer-item">
                <div class="web-balloon left-top secondary padding-large">
                    {Build.isBrowser &&
                        user.isLoggedIn() &&
                        `${this.texts?.helloText}, ${
                            user.isMale() ? this.texts?.maleText : this.texts?.femaleText
                        } ${user.getLastName()}. `}
                    {this.texts?.startupText ?? '...'}
                </div>
            </div>
            {this.items.map(this.renderItem)}
        </div>
    );
}
